
import React, { useState, useEffect, useRef } from 'react';
import { AlertCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Button } from './ui/button';
import { useToast } from '@/hooks/use-toast';
import { handleError } from '@/utils/error-handling';
import BlockStackGame from './BlockStackGame';
import AsteroidsGame from './AsteroidsGame';
import BreathingGame from './BreathingGame';
import MatchingGame from './MatchingGame';
import { useIsMobile } from '@/utils/mobile';
import ErrorBoundary from './ErrorBoundary';
import MobileInstructions from './games/common/MobileInstructions';

interface GameFrameProps {
  title: string;
  url: string;
  isCustom: boolean;
}

const GameFrame = ({ title, url, isCustom }: GameFrameProps) => {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const isMobile = useIsMobile();
  const { toast } = useToast();
  
  useEffect(() => {
    // Check for any known problematic browser
    const isOperaBrowser = /Opera|OPR|Opera Mini/i.test(navigator.userAgent);
    const isOlderBrowser = /MSIE|Trident|Edge\/\d+/i.test(navigator.userAgent);
    
    if ((isOperaBrowser || isOlderBrowser) && isCustom) {
      console.log("Potentially incompatible browser detected, showing compatibility notice");
      toast({
        title: "Browser Compatibility Notice",
        description: "Your browser may have limited compatibility with some games. If you experience issues, try Chrome or Firefox.",
        duration: 7000,
      });
    }
    
    // Log environment for debugging
    console.log("Environment:", process.env.NODE_ENV);
    console.log("Current URL:", window.location.href);
    console.log("User agent:", navigator.userAgent);
  }, [toast, isCustom]);
  
  if (isCustom) {
    const gameComponents = {
      "Block Stack": <BlockStackGame title="Block Stack" />,
      "Asteroid Field": <AsteroidsGame title="Asteroid Field" />,
      "Breathing Exercise": <BreathingGame title="Breathing Exercise" />,
      "Match & Meditate": <MatchingGame title="Match & Meditate" />
    };
    
    const GameComponent = gameComponents[title] || <BlockStackGame title="Block Stack" />;
    
    return (
      <ErrorBoundary
        fallback={
          <div className="w-full max-w-4xl h-[70vh] bg-white rounded-lg shadow-lg overflow-hidden flex flex-col items-center justify-center p-6">
            <Alert variant="destructive" className="max-w-md mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Game Error</AlertTitle>
              <AlertDescription>An unexpected error occurred in the game.</AlertDescription>
            </Alert>
            <p className="text-gray-600 mb-4 text-center">
              We encountered an issue with this game. Please try refreshing the page or choose a different game.
              {/Opera|OPR|Opera Mini/i.test(navigator.userAgent) && 
                " Opera browser may have limited compatibility with some games. Consider using Chrome or Firefox for the best experience."}
            </p>
            <div className="flex gap-4">
              <Button onClick={() => window.location.reload()} className="mt-4">Refresh Page</Button>
              <Button variant="outline" onClick={() => window.location.href = '/'} className="mt-4">Back to Games</Button>
            </div>
          </div>
        }
      >
        {GameComponent}
      </ErrorBoundary>
    );
  }

  useEffect(() => {
    try {
      if (!url) {
        throw new Error("Game URL is missing");
      }
      
      setError(null);
      setIsLoading(true);
      
      console.log(`Loading game: ${title} from URL: ${url}`);
      console.log(`Browser info: ${navigator.userAgent}`);
      console.log(`Environment: ${process.env.NODE_ENV}`);
      console.log(`Current page URL: ${window.location.href}`);
    } catch (err) {
      console.error("Invalid game URL:", url, err);
      setError("Invalid game URL. Please try another game.");
      setIsLoading(false);
      handleError(err, `Failed to load game: ${title}`);
    }
  }, [url, title]);

  const handleIframeLoad = () => {
    setIsLoading(false);
    console.log("Game iframe loaded successfully:", title);
    toast({
      title: "Game Loaded",
      description: `${title} is ready to play!`
    });
  };

  const handleIframeError = () => {
    const errorMsg = `Error loading game: ${title}`;
    console.error(errorMsg);
    setError(
      "Unable to load the game. This may be due to the game being unavailable or blocked by browser security settings. Please try another game."
    );
    setIsLoading(false);
    handleError(new Error(errorMsg));
  };

  const handleRetry = () => {
    setError(null);
    setIsLoading(true);
    if (iframeRef.current) {
      const currentSrc = iframeRef.current.src;
      iframeRef.current.src = "";
      setTimeout(() => {
        if (iframeRef.current) {
          iframeRef.current.src = currentSrc;
        }
      }, 500);
      toast({
        title: "Retrying",
        description: `Attempting to reload ${title}...`
      });
    }
  };

  const handleBackToGames = () => {
    window.location.href = '/';
  };

  if (error) {
    return (
      <div 
        className="w-full max-w-4xl h-[70vh] bg-white rounded-lg shadow-lg overflow-hidden flex flex-col items-center justify-center p-6"
        role="alert"
        aria-live="assertive"
      >
        <Alert variant="destructive" className="max-w-md mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Game Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <p className="text-gray-600 mb-4 text-center">
          If you're experiencing issues with this game, it may be temporarily unavailable.
          Please try a different game from our selection.
          {/Opera|OPR|Opera Mini/i.test(navigator.userAgent) && 
            " Opera browser may have limited compatibility with some games. Consider using Chrome or Firefox for the best experience."}
        </p>
        <div className="flex gap-4">
          <Button onClick={handleRetry} aria-label={`Retry loading ${title}`}>Retry</Button>
          <Button variant="outline" onClick={handleBackToGames} aria-label="Return to game selection">Back to Games</Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full max-w-4xl ${isMobile ? 'h-[60vh]' : 'h-[70vh]'} bg-white rounded-lg shadow-lg overflow-hidden relative`}>
      {isLoading && (
        <div className="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center z-10">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-coral border-r-transparent mb-4"></div>
            <p>Loading game: {title}</p>
            <p className="text-xs text-gray-500 mt-2">This may take a few moments...</p>
          </div>
        </div>
      )}
      <iframe 
        ref={iframeRef}
        src={url}
        title={title}
        className="w-full h-full border-0"
        allowFullScreen
        allow="autoplay; fullscreen"
        onLoad={handleIframeLoad}
        onError={handleIframeError}
        sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-downloads"
      ></iframe>
      {isMobile && title === "Block Stack" && (
        <MobileInstructions 
          gameType="BlockStack"
          className="fixed bottom-0 left-0 right-0 bg-yellow-50 p-3 text-sm text-center shadow-lg z-10"
        />
      )}
    </div>
  );
};

export default GameFrame;
